import React from "react";
import { Badge, Text, VStack, Icon } from "@chakra-ui/react";
import { FaCheckCircle, FaClock, FaExclamationCircle } from "react-icons/fa";
import { SuprsendPerEntityStatus } from "./utils/broadcastUtils";

interface BroadcastStatusBadgeProps {
  status?: SuprsendPerEntityStatus;
  formatDateTime: (value?: string | null) => string;
}

type StatusType = "responded" | "expired" | "sent" | "none";

interface StatusConfig {
  colorScheme: string;
  icon: React.ComponentType;
  label: string;
  getTimestamp: (status: SuprsendPerEntityStatus) => string | null;
}

const STATUS_CONFIGS: Record<StatusType, StatusConfig> = {
  responded: {
    colorScheme: "green",
    icon: FaCheckCircle,
    label: "Responded",
    getTimestamp: (status) => status.respondedAt || null,
  },
  expired: {
    colorScheme: "red",
    icon: FaExclamationCircle,
    label: "Expired",
    getTimestamp: (status) => status.expiresAt || null,
  },
  sent: {
    colorScheme: "orange",
    icon: FaClock,
    label: "Sent",
    getTimestamp: (status) => status.sentAt || null,
  },
  none: {
    colorScheme: "gray",
    icon: FaClock,
    label: "—",
    getTimestamp: () => null,
  },
};

function determineStatusType(
  status: SuprsendPerEntityStatus | undefined,
  isExpired: boolean,
): StatusType {
  if (!status) return "none";
  if (status.respondedAt) return "responded";
  if (isExpired) return "expired";
  if (status.sentAt) return "sent";
  return "none";
}

export const BroadcastStatusBadge: React.FC<BroadcastStatusBadgeProps> = ({
  status,
  formatDateTime,
}) => {
  if (!status) {
    return (
      <Text fontSize="0.75rem" color="gray.400">
        —
      </Text>
    );
  }

  const now = new Date();
  const expiresAtDate = status.expiresAt ? new Date(status.expiresAt) : null;
  const isExpired =
    !!expiresAtDate && !Number.isNaN(expiresAtDate.getTime())
      ? now >= expiresAtDate
      : false;

  const statusType = determineStatusType(status, isExpired);
  const config = STATUS_CONFIGS[statusType];

  if (statusType === "none") {
    return (
      <Text fontSize="0.75rem" color="gray.400">
        {config.label}
      </Text>
    );
  }

  const timestamp = config.getTimestamp(status);

  return (
    <VStack spacing={1}>
      <Badge
        colorScheme={config.colorScheme}
        fontSize="xs"
        px={2}
        py={0.5}
        borderRadius="full"
        display="flex"
        alignItems="center"
        gap={1}
      >
        <Icon as={config.icon} boxSize={3} /> {config.label}{" "}
        {timestamp && `(${formatDateTime(timestamp)})`}
      </Badge>
    </VStack>
  );
};
